import * as React from 'react';
import styled from 'styled-components';

import { InfoBox } from '@longline/aqua-ui/containers/InfoBox';
import { TertiaryButton } from '@longline/aqua-ui/controls/TertiaryButton';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { Form } from '@longline/aqua-ui/containers/Form';
import { Dropdown } from '@longline/aqua-ui/inputs/Dropdown';
import { Gradient } from '@longline/aqua-ui/controls/Gradient';
import { IGradientStop } from '@longline/aqua-ui/Types';
import { Slider } from '@longline/aqua-ui/inputs/Slider';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { IReference } from '../../types/IReference';
import { ReferenceInfoBoxHeader } from './ReferenceInfoBoxHeader';
import { PresetGradients } from '../../types/ILayerTypeDefinition';

interface IProps {
  value?: IReference;
}

const ReferenceInfoBox = (props: IProps) => {
  const globalState = useGlobalState();

  // Optional because <ListView aux> injects the active item; render nothing
  // if no item is selected.
  if (!props.value) return null;
  const reference = props.value;

  const handleUpdateOpacity = (value: number) => {
    reference.opacity = value;
    globalState.updateReference(reference);
  }

  const handleUpdateGradient = (value: IGradientStop[]) => {
    reference.gradient = value;
    globalState.updateReference(reference);
  }

  const handleUpdateProperty = (value: string) => {
    reference.property = value;
    globalState.updateReference(reference);
  }

  return (
    <InfoBox 
      scroll
      minHeight={400}
      padded    
      header={<ReferenceInfoBoxHeader value={reference}/>}
      width={300}
      footer={<>
        <TertiaryButton onClick={() => globalState.toggleReference(reference)}>TOGGLE</TertiaryButton>
        <TertiaryButton onClick={() => globalState.focusReference(reference)}>FOCUS</TertiaryButton>
      </>}
    >
      <InfoBox.Row name="Source" value={reference.name}/>
      <InfoBox.Row name="Features" value={<NumberFormatter value={reference.geojson?.features.length ?? 0} decimals={0}/>}/>
      <InfoBox.Row name="Properties" value={<NumberFormatter value={reference.properties.length} decimals={0}/>}/>

      <Spacer/>

      <Form.Field
        label="Opacity" 
        name="opacity"
        hint="Opacity of this layer."
        wrapper='box'
        value={reference.opacity}
        onChange={handleUpdateOpacity}
        rules={{ required: {} }}
        control={<Slider padded min={0} max={100} ticks={11} snapToTicks />}
      />

      <Form.Field
        label="Gradient"
        name="gradient"
        hint="Gradient used to render this layer."
        wrapper="box"
        value={reference.gradient}
        onChange={handleUpdateGradient}
        rules={{ required: {} }}
        control={<Dropdown direction='up' maxItems={5} data={[ PresetGradients.Grey, PresetGradients.Blue, PresetGradients.Red, PresetGradients.DustyCactus, PresetGradients.Mimosa, PresetGradients.MelloYellow, PresetGradients.RetroWagon, PresetGradients.Rainbow]} label={(item) => <Gradient checkered rounded thickness={10} gradientStops={item}/>}>
          <Dropdown.Column>{(item) => <Gradient checkered rounded thickness={10} gradientStops={item}/>}</Dropdown.Column>
        </Dropdown>}
      />

      <Form.Field
        label="Property"
        name="property"
        hint="Numeric property to shade by."
        wrapper="box"
        value={reference.property}
        onChange={handleUpdateProperty}
        control={<Dropdown clearable maxItems={5} direction='up' data={reference.properties} label={(item) => item} placeholder="(none)">
          <Dropdown.Column>{(item) => item}</Dropdown.Column>
        </Dropdown>}
      />
    </InfoBox>
  )
}

const Spacer = styled.div`
  height: 8px;
`

export { ReferenceInfoBox }
